import { useMutation, useQuery } from "@apollo/client/react"
import { ExternalLink, FileText, Plus, RefreshCw, Rss } from "lucide-react"
import { useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { toast } from "sonner"
import { useViewer } from "~/auth/use-viewer"
import { experienceProfilePath, newIdeaPath, postPath, sourceReactionPath } from "~/common/paths"
import { useDocumentTitle } from "~/common/use-document-title"
import { PageLayout } from "~/layouts/page-layout"
import { Button } from "~/ui/button"
import { GraphqlError } from "~/ui/graphql-error"
import { WORKSPACE, REFRESH } from "./editorial-queries"
import { SourceActions } from "./source-actions"
import { SuggestionBrief } from "./suggestion-brief"
import { MATCH_SOURCES } from "./suggestion-queries"

type Tab = "suggested" | "drafts" | "ready" | "sources"

export const DashboardScreen = () => {
  useDocumentTitle("Dashboard")
  const { viewer } = useViewer()
  const [tab, setTab] = useState<Tab>("suggested")
  const [sourceView, setSourceView] = useState("all")
  const [search, setSearch] = useState("")
  const [sourceLimit, setSourceLimit] = useState(20)
  const [mineOnly, setMineOnly] = useState(true)
  const navigate = useNavigate()
  const { data, error, loading, refetch } = useQuery(WORKSPACE, { pollInterval: 15000 })
  const [refreshSources, { loading: refreshing }] = useMutation(REFRESH)
  const [matchSources, { loading: matching }] = useMutation(MATCH_SOURCES)
  const [matchError, setMatchError] = useState<Error | null>(null)
  const [refreshError, setRefreshError] = useState<Error | null>(null)
  const workspace = data?.editorialWorkspace
  const posts = workspace?.posts.filter((p) => !mineOnly || p.editable) ?? []
  const draftPosts = posts.filter((p) => p.status === "DRAFT")
  const readyPosts = posts.filter((p) => p.status === "APPROVED")
  const query = search.toLowerCase()
  const visibleSources =
    workspace?.sources
      .filter(
        (s) =>
          `${s.title} ${s.summary} ${s.feedSource?.name}`.toLowerCase().includes(query) &&
          (sourceView === "dismissed"
            ? s.decision?.status === "dismissed"
            : s.decision?.status !== "dismissed") &&
          (sourceView !== "saved" || s.decision?.status === "saved") &&
          (sourceView !== "matched" || s.suggestion?.current)
      )
      .sort(
        (a, b) =>
          (b.suggestion?.current ? b.suggestion.score : 0) -
          (a.suggestion?.current ? a.suggestion.score : 0)
      ) ?? []
  const visiblePosts = (tab === "ready" ? readyPosts : draftPosts).filter((p) =>
    `${p.title} ${p.body} ${p.authorName}`.toLowerCase().includes(query)
  )

  const refresh = async () => {
    setRefreshError(null)
    try {
      const result = await refreshSources()
      if (result.error) throw result.error
      toast.success("Source refresh queued. New stories will appear here shortly.")
      await refetch()
    } catch (err) {
      setRefreshError(err instanceof Error ? err : new Error("Could not refresh sources"))
    }
  }

  const match = async () => {
    setMatchError(null)
    try {
      const result = await matchSources()
      if (result.error) throw result.error
      toast.success("Matching your profile to recent stories. Results will appear shortly.")
      await refetch()
    } catch (err) {
      setMatchError(err instanceof Error ? err : new Error("Could not match sources"))
    }
  }
  const matchPending = ["queued", "matching"].includes(workspace?.suggestionRun?.status ?? "")

  return (
    <PageLayout
      title="Social Coordinator"
      actions={
        <Button onClick={() => navigate(newIdeaPath({}))}>
          <Plus className="size-4" />
          New idea
        </Button>
      }
    >
      <div className="mx-auto max-w-7xl space-y-7 pb-12">
        <div className="rounded-2xl bg-primary p-7 text-primary-foreground sm:p-9">
          <p className="text-xs font-semibold uppercase tracking-widest opacity-70">
            Tanooki Labs · Editorial workspace
          </p>
          <h2 className="mt-3 text-3xl font-medium tracking-tight">
            Your next useful conversation.
          </h2>
          <p className="mt-3 max-w-2xl text-sm leading-6 opacity-80">
            Collect an idea, bring your experience, and shape a post in your own words. Your queue
            is personal. Your next recording batch is Friday morning.
          </p>
          <div className="mt-6 flex flex-wrap gap-6 text-sm">
            <span>
              {draftPosts.length} {draftPosts.length === 1 ? "draft" : "drafts"} in progress
            </span>
            <span>{readyPosts.length} ready to post</span>
            <span>{workspace?.sources.length ?? 0} source stories</span>
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap gap-2" aria-label="Workspace queues">
            {(
              [
                ["suggested", "Suggested & saved"],
                ["drafts", "In progress"],
                ["ready", "Ready to post"],
                ["sources", "Sources & setup"],
              ] as const
            ).map(([value, label]) => (
              <Button
                key={value}
                variant={tab === value ? "default" : "outline"}
                onClick={() => setTab(value)}
                aria-pressed={tab === value}
              >
                {label}
              </Button>
            ))}
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={mineOnly}
              onChange={(e) => setMineOnly(e.target.checked)}
            />
            My posts only
          </label>
        </div>
        {error && <GraphqlError error={error} />}
        {loading && !workspace && <p role="status">Loading your workspace…</p>}
        <div className="space-y-4">
          <div className="space-y-4">
            {tab !== "sources" && (
              <input
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-ring disabled:opacity-50"
                aria-label="Search workspace"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search topics, sources, or post text"
              />
            )}
            {tab === "suggested" && (
              <>
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm text-muted-foreground">
                    {workspace?.hasExperienceProfile
                      ? "Ideas connected to your documented experience. Choose your own perspective."
                      : "Add your experience profile to discover stories with a documented personal fit."}
                  </p>
                  <Button size="sm" variant="outline" onClick={refresh} disabled={refreshing}>
                    <RefreshCw className="size-4" />
                    Refresh
                  </Button>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {[
                    ["all", "All sources"],
                    ["matched", "Matched to me"],
                    ["saved", "Saved"],
                    ["dismissed", "Dismissed"],
                  ].map(([value, label]) => (
                    <Button
                      key={value}
                      size="sm"
                      variant={sourceView === value ? "secondary" : "ghost"}
                      onClick={() => setSourceView(value)}
                      aria-pressed={sourceView === value}
                    >
                      {label}
                    </Button>
                  ))}
                  {workspace?.hasExperienceProfile ? (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={matching || matchPending}
                      onClick={() => void match()}
                    >
                      {matchPending ? "Matching stories…" : "Match my experience"}
                    </Button>
                  ) : (
                    <Button size="sm" variant="outline" asChild>
                      <Link to={experienceProfilePath({})}>Add experience profile</Link>
                    </Button>
                  )}
                </div>
                {workspace?.suggestionRun?.status === "failed" && (
                  <p role="alert" className="text-sm text-destructive">
                    {workspace.suggestionRun.error}
                  </p>
                )}
                {matchError && <GraphqlError error={matchError} />}
                {refreshError && <GraphqlError error={refreshError} />}
                {!visibleSources.length && !loading && (
                  <div className="rounded-xl border border-dashed p-9 text-center">
                    <Rss className="mx-auto mb-3 size-6 text-muted-foreground" />
                    <h3 className="font-semibold">
                      {sourceView === "matched"
                        ? "No documented matches yet."
                        : "Make room for a good idea."}
                    </h3>
                    <p className="mt-2 text-sm text-muted-foreground">
                      {sourceView === "matched"
                        ? "Add concrete experience to your profile, then match stories. We leave this empty when there is no supported connection."
                        : "Refresh sources to collect headlines, or start with something you already want to talk about."}
                    </p>
                  </div>
                )}
                <div className="grid gap-4 lg:grid-cols-2">
                  {visibleSources.slice(0, sourceLimit).map((source) => (
                    <article
                      key={source.id}
                      className="flex flex-col rounded-xl border bg-card p-5"
                    >
                      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        {source.feedSource?.name ?? "Original idea"}
                      </p>
                      <h3 className="mt-3 text-lg font-semibold leading-snug">{source.title}</h3>
                      {source.summary && (
                        <p className="mt-3 line-clamp-3 text-sm leading-6 text-muted-foreground">
                          {source.summary}
                        </p>
                      )}
                      {source.suggestion && (
                        <details className="mt-4 rounded-lg bg-muted/50 p-3">
                          <summary className="cursor-pointer text-sm font-medium">
                            {source.suggestion.current
                              ? "Why this fits your experience"
                              : "Previous profile match"}
                            {source.suggestion.breakingCandidate && source.suggestion.current
                              ? " · timely"
                              : ""}
                          </summary>
                          <SuggestionBrief source={source} />
                        </details>
                      )}
                      <div className="mt-auto flex items-center justify-between gap-3 pt-5">
                        <a
                          href={source.url}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-sm underline underline-offset-4"
                        >
                          Read source <ExternalLink className="size-3" />
                        </a>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => navigate(sourceReactionPath({ sourceId: source.id }))}
                        >
                          Start a post
                        </Button>
                      </div>
                      <SourceActions source={source} />
                    </article>
                  ))}
                </div>
              </>
            )}
            {tab === "suggested" && visibleSources.length > sourceLimit && (
              <Button variant="outline" onClick={() => setSourceLimit((limit) => limit + 20)}>
                Show more stories ({visibleSources.length - sourceLimit} remaining)
              </Button>
            )}
            {(tab === "drafts" || tab === "ready") && (
              <>
                <p className="text-sm text-muted-foreground">
                  {tab === "drafts"
                    ? "Your own words, with every saved version preserved."
                    : "Approved for manual posting. Copying text does not mark a post as published."}
                </p>
                {!visiblePosts.length && (
                  <div className="rounded-xl border border-dashed p-9 text-center">
                    <FileText className="mx-auto mb-3 size-6 text-muted-foreground" />
                    <h3 className="font-semibold">
                      {tab === "ready" ? "Nothing approved yet." : "Your first draft starts here."}
                    </h3>
                    <p className="mt-2 text-sm text-muted-foreground">
                      {tab === "ready"
                        ? "Review a saved draft and approve it when it sounds like you."
                        : "Choose a source story or add an original idea."}
                    </p>
                  </div>
                )}
                {visiblePosts.map((post) => (
                  <button
                    key={post.id}
                    onClick={() => navigate(postPath({ postId: post.id }))}
                    className="block w-full rounded-xl border bg-card p-5 text-left transition-colors hover:bg-muted focus-visible:outline-ring"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <h3 className="font-semibold">{post.title}</h3>
                      <span className="rounded-full bg-secondary px-2 py-1 text-xs">
                        {post.status.toLowerCase()}
                      </span>
                    </div>
                    <p className="mt-2 text-xs text-muted-foreground">
                      {post.authorName} · {post.revisions.length} saved versions
                    </p>
                    <p className="mt-3 line-clamp-3 text-sm leading-6 text-muted-foreground">
                      {post.body || "No post text yet."}
                    </p>
                  </button>
                ))}
              </>
            )}
            {tab === "sources" && (
              <div className="space-y-5">
                <div className="rounded-xl border p-5">
                  <h3 className="font-semibold">Sources that fit the work</h3>
                  <p className="mt-2 text-sm leading-6 text-muted-foreground">
                    AI and agents, useful technology, small-business operations, product thinking,
                    and changing design/development practices. Collection runs quietly; weekly
                    recording nudges are planned for Friday mornings.
                  </p>
                  <div className="mt-5 space-y-4">
                    {workspace?.feeds.map((feed) => (
                      <div key={feed.id} className="border-t pt-3">
                        <p className="text-sm font-medium">{feed.name}</p>
                        <p className="mt-1 text-xs text-muted-foreground">
                          {feed.lastError
                            ? `Refresh needs attention: ${feed.lastError}`
                            : feed.lastSuccessAt
                              ? "Latest refresh succeeded"
                              : "Awaiting first refresh"}
                        </p>
                      </div>
                    ))}
                  </div>
                  <Button
                    className="mt-5"
                    variant="outline"
                    onClick={refresh}
                    disabled={refreshing}
                  >
                    Refresh sources
                  </Button>
                  {refreshError && <GraphqlError error={refreshError} />}
                </div>
                <div className="rounded-xl border p-5">
                  <h3 className="font-semibold">Next integrations</h3>
                  <p className="mt-3 text-sm">
                    Reap: {workspace?.reapConfigured ? "API key configured" : "API key needed"}
                  </p>
                  <p className="mt-2 text-sm">
                    Pangram:{" "}
                    {workspace?.pangramConfigured ? "API key configured" : "API key needed"}
                  </p>
                  <p className="mt-3 text-sm text-muted-foreground">
                    Provider keys stay on the server. Live processing and delivery require working
                    provider accounts and reachable media storage.
                  </p>
                </div>
                <div className="rounded-xl bg-muted p-5 text-sm">
                  <strong>{viewer.firstName}, your writing stays yours.</strong>
                  <p className="mt-2 text-muted-foreground">
                    Cadence and approval are per partner. Pangram is a signal beside the editor,
                    with at most two automatic revisions. Manual editing remains available.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </PageLayout>
  )
}
